import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import { PoCheckboxGroupOption } from "@po-ui/ng-components";

import { MainService } from "src/app/app.service";
import { resolveRoleTag } from "src/app/shared/role-tags";

export interface AccessCatalogEntry {
  key: string;
  label: string;
  group: string;
  dependsOn?: string;
}

export const HIDDEN_ACCESS_KEYS: ReadonlyArray<string> = [];

@Injectable()
export class AccessCatalogService {
  private catalog$?: Observable<Array<AccessCatalogEntry>>;

  constructor(
    private http: HttpClient,
    private mainService: MainService,
  ) {}

  getCatalog(): Observable<Array<AccessCatalogEntry>> {
    if (!this.catalog$) {
      this.catalog$ = this.http
        .get<Array<AccessCatalogEntry>>("/api/v2/access/catalog", this.mainService.httpOptions)
        .pipe(
          map((catalog) => catalog.filter((entry) => !HIDDEN_ACCESS_KEYS.includes(entry.key))),
          // nome amigavel igual ao do perfil ("Smart Review · Run"); a chave fica em entry.key
          map((catalog) => catalog.map((entry) => ({ ...entry, label: resolveRoleTag(entry.key).label }))),
          // Falha não fica em cache: o próximo getCatalog (ex.: Atualizar) tenta de novo
          catchError(() => {
            this.catalog$ = undefined;
            return of([]);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
    return this.catalog$;
  }

  toCheckboxOptions(catalog: Array<AccessCatalogEntry>): Array<PoCheckboxGroupOption> {
    return catalog.map((r) => ({ label: r.label, value: r.key }));
  }

  applyDependencies(
    catalog: Array<AccessCatalogEntry>,
    next: Array<string>,
    prev: Array<string>,
  ): Array<string> {
    let result = [...next];

    catalog.forEach((entry) => {
      if (!entry.dependsOn) {
        return;
      }
      const nowChecked = result.includes(entry.key) && !prev.includes(entry.key);
      if (nowChecked && !result.includes(entry.dependsOn)) {
        result = [...result, entry.dependsOn];
      }
    });

    catalog.forEach((entry) => {
      if (!entry.dependsOn) {
        return;
      }
      const dependencyUnchecked = !result.includes(entry.dependsOn) && prev.includes(entry.dependsOn);
      if (dependencyUnchecked) {
        result = result.filter((r) => r !== entry.key);
      }
    });

    return result;
  }
}
